import { Box, Chip, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { DATE_FORMAT } from '@/config';
import { ISexOffender, IUser, RiskLevelLabels } from '@/models';

const sexOffenderId = (): Partial<GridColDef> => ({
  field: 'sexOffenderId',
  renderCell: (params) => params.row.sexOffenderId || '-',
  sortable: true,
});

const name = (): Partial<GridColDef> => ({
  field: 'name',
  sortable: false,
  renderCell: (params: { row: ISexOffender }) => {
    const person = params.row.person as any;
    if (!person || typeof person === 'string') return '-';
    return person.fullName || person.email;
  },
});

const riskLevel = (): Partial<GridColDef> => ({
  field: 'riskLevel',
  renderCell: (params) =>
    params.row.riskLevel !== undefined && params.row.riskLevel !== null
      ? RiskLevelLabels[params.row.riskLevel] ?? '-'
      : '-',
  sortable: true,
});

const assignedOfficer = (): Partial<GridColDef> => ({
  field: 'assignedOfficer',
  sortable: false,
  renderCell: (params: { row: ISexOffender }) => {
    const officer = params.row.assignedOfficer as IUser | undefined;
    if (!officer) return '-';
    return officer.fullName || officer.email || '-';
  },
});

const active = (): Partial<GridColDef> => ({
  field: 'active',
  renderCell: (params: { row: ISexOffender }) => (
    <Chip
      label={params.row.active ? 'Active' : 'Inactive'}
      color={params.row.active ? 'secondary' : 'error'}
      size="small"
      sx={{ borderRadius: '4px', color: 'white', fontWeight: 500 }}
    />
  ),
  sortable: true,
});

const nextDueDate = (): Partial<GridColDef> => ({
  field: 'nextDueDate',
  renderCell: (params: { row: ISexOffender }) => {
    if (!params.row.nextDueDate) return '-';
    const dateStr = moment(params.row.nextDueDate).format(DATE_FORMAT);
    const daysFromNow = moment(params.row.nextDueDate)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
    const isOverdue = daysFromNow < 0;
    const isCurrentDay = daysFromNow === 0;
    const dayCount = Math.abs(daysFromNow);
    const suffix = isOverdue
      ? `${dayCount} day${dayCount !== 1 ? 's' : ''} overdue`
      : `${daysFromNow} day${daysFromNow !== 1 ? 's' : ''} remaining`;

    return (
      <Box sx={{ color: isOverdue ? 'error.main' : 'inherit' }}>
        <Box
          component="span"
          sx={{ color: isCurrentDay && !isOverdue ? 'error.main' : 'inherit' }}
        >
          {dateStr}
        </Box>{' '}
        ({suffix})
      </Box>
    );
  },
  sortable: true,
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
): Partial<GridColDef> => ({
  field: 'actions',
  sortable: false,
  renderCell: (params) => (
    <IconButton size="small" onClick={(e) => openMenu?.(e, params.row)}>
      <IcDotSpread />
    </IconButton>
  ),
});

export const sexOffenderCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void,
): Record<string, () => Partial<GridColDef>> => ({
  sexOffenderId,
  name,
  riskLevel,
  assignedOfficer,
  active,
  nextDueDate,
  actions: () => actions(openMenu),
});
